import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');

const BTN = 'select-none touch-none flex items-center justify-center font-extrabold transition-all active:scale-90';

export default function GamepadController() {
  const { room } = useParams();
  const [status, setStatus] = useState('connecting'); // connecting | connected | closed
  const [player, setPlayer] = useState(null);
  const wsRef = useRef(null);
  const pressed = useRef({});

  useEffect(() => {
    const ws = new WebSocket(`${WS_BASE}/api/gamepad/ws/controller/${room}`);
    wsRef.current = ws;
    ws.onopen = () => setStatus('connected');
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === 'joined') setPlayer(d.player);
      } catch { /* ignore */ }
    };
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => setStatus('closed');
    return () => ws.close();
  }, [room]);

  const send = useCallback((type, key) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ source: 'mayaplus-gamepad', type, key }));
  }, []);

  const bind = (key) => ({
    onPointerDown: (e) => {
      e.preventDefault();
      if (pressed.current[key]) return;
      pressed.current[key] = true;
      if (navigator.vibrate) navigator.vibrate(15);
      send('press', key);
    },
    onPointerUp: (e) => { e.preventDefault(); pressed.current[key] = false; send('release', key); },
    onPointerLeave: () => { if (pressed.current[key]) { pressed.current[key] = false; send('release', key); } },
    onContextMenu: (e) => e.preventDefault(),
  });

  return (
    <div className="fixed inset-0 flex flex-col select-none" style={{ background: 'linear-gradient(180deg, #11041F 0%, #1c0f30 100%)', touchAction: 'none' }} data-testid="gamepad-controller-page">
      {/* Status bar */}
      <div className="flex items-center justify-between px-5 py-3">
        <span className="text-[#E9641A] font-extrabold text-lg tracking-wide">MAYA+ <span className="text-white/60 font-semibold text-sm">Manette</span></span>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${status === 'connected' ? 'bg-green-500/15 text-green-400' : status === 'closed' ? 'bg-red-500/15 text-red-400' : 'bg-white/10 text-white/50'}`} data-testid="gamepad-status">
          {status === 'connected' ? `Connectée · Joueur ${player || 1}` : status === 'closed' ? 'Déconnectée — rescannez le QR code' : 'Connexion…'}
        </span>
      </div>

      <div className="flex-1 flex items-center justify-between px-6 pb-8 gap-4">
        {/* D-Pad */}
        <div className="relative w-56 h-56 flex-none" data-testid="gamepad-dpad">
          <button {...bind('up')} data-testid="pad-up" className={`${BTN} absolute left-1/2 top-0 -translate-x-1/2 w-[72px] h-[76px] bg-white/10 rounded-t-2xl text-white text-2xl active:bg-[#E9641A]`}>▲</button>
          <button {...bind('down')} data-testid="pad-down" className={`${BTN} absolute left-1/2 bottom-0 -translate-x-1/2 w-[72px] h-[76px] bg-white/10 rounded-b-2xl text-white text-2xl active:bg-[#E9641A]`}>▼</button>
          <button {...bind('left')} data-testid="pad-left" className={`${BTN} absolute top-1/2 left-0 -translate-y-1/2 w-[76px] h-[72px] bg-white/10 rounded-l-2xl text-white text-2xl active:bg-[#E9641A]`}>◀</button>
          <button {...bind('right')} data-testid="pad-right" className={`${BTN} absolute top-1/2 right-0 -translate-y-1/2 w-[76px] h-[72px] bg-white/10 rounded-r-2xl text-white text-2xl active:bg-[#E9641A]`}>▶</button>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[72px] h-[72px] bg-white/5" />
        </div>

        {/* Start */}
        <button {...bind('start')} data-testid="pad-start" className={`${BTN} px-6 py-2.5 bg-white/10 rounded-full text-white/70 text-xs uppercase tracking-widest active:bg-[#E9641A] active:text-white`}>Start</button>

        {/* A / B */}
        <div className="relative w-52 h-52 flex-none" data-testid="gamepad-buttons">
          <button {...bind('b')} data-testid="pad-b" className={`${BTN} absolute left-0 bottom-4 w-[88px] h-[88px] rounded-full bg-[#3b82f6]/25 border-2 border-[#3b82f6]/50 text-[#93c5fd] text-3xl active:bg-[#3b82f6]`}>B</button>
          <button {...bind('a')} data-testid="pad-a" className={`${BTN} absolute right-0 top-4 w-[88px] h-[88px] rounded-full bg-[#E9641A]/25 border-2 border-[#E9641A]/60 text-[#FE975E] text-3xl active:bg-[#E9641A] active:text-white`}>A</button>
        </div>
      </div>

      <p className="text-center text-white/25 text-[10px] pb-3">Gardez cet écran ouvert pendant le jeu · A = action / saut · B = secondaire</p>
    </div>
  );
}
